import './App.css';
import './csscomponent/MobileAppShell.css';
import {
  BrowserRouter,
  HashRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Suspense, lazy, useEffect, useState } from 'react';
import { useAuth } from './lib/authSession';
import { Capacitor } from '@capacitor/core';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Header from './component/Header';
import Footer from './component/Footer';
import ErrorBoundary from './component/ErrorBoundary';
import Home from './component/Home';
import HeroSection from './component/HeroSection';
import Services from './component/Services';
import About from './component/About';
import Brochure from './component/Brochure';
import Msme from './component/Msme';
import Slidingimages from './component/Slidingimages';
import Testimonial from './component/Testimonial';
import ProductPage from './component/product/ProductPage';
import JobApply from './component/JobApply';
import InternshipVerification from './component/InternshipVerification';
import Legal from './component/Legal';
import PrivacyPolicy from './component/PrivacyPolicy';
import TermsOfService from './component/TermsOfService';
import Contact from './component/Contact';
import PackageDetails from './component/PackageDetails';
import PaymentPage from './component/PaymentPage';
import PaymentSuccess from './component/PaymentSuccess';
import DynamicPage from './component/cms/DynamicPage';
import BrandLoader from './component/mobile/BrandLoader';
import { preloadInterstitialAd } from './lib/admobService';
import AppShell from './component/mobile/AppShell';
import AppSplashScreen from './component/mobile/AppSplashScreen';
import Login from './component/Login';
import Signup from './component/Signup';
import ForgotPassword from './component/ForgotPassword';
import ResetPassword from './component/ResetPassword';
import VerifyEmail from './component/VerifyEmail';
import { appBase } from './lib/publicPath';

const UserDashboard = lazy(() => import('./component/UserDashboard'));
const AdminDashboard = lazy(() => import('./component/AdminDashboard'));
const SKIP_SPLASH_ONCE_KEY = 'sd_skip_native_splash_once';

function consumeOneTimeSplashSkipFlag() {
  if (typeof window === 'undefined') return false;

  try {
    const shouldSkip = localStorage.getItem(SKIP_SPLASH_ONCE_KEY) === 'true';
    if (shouldSkip) {
      localStorage.removeItem(SKIP_SPLASH_ONCE_KEY);
      return true;
    }
  } catch (_) {}

  return false;
}

function PublicHomePage() {
  return (
    <ErrorBoundary
      fallback={(
        <div style={{ padding: '6rem 1rem', textAlign: 'center' }}>
          <h2>Welcome to Services &amp; Developer</h2>
          <p>Please refresh the page to reload the home sections.</p>
        </div>
      )}
    >
      <>
        <Home />
        <HeroSection />
        <Slidingimages />
        <Msme />
        <Services isHomeSection />
        <Testimonial />
      </>
    </ErrorBoundary>
  );
}

function RequireAuth({ children }) {
  const { loading, isAuthenticated } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { loading, isAuthenticated, isAdmin } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppLayout() {
  const location = useLocation();
  const { loading, isAuthenticated } = useAuth();
  const authRoutes = ['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
  const hideHeader = authRoutes.includes(location.pathname);
  const hideFooter = hideHeader
    || location.pathname.startsWith('/dashboard')
    || location.pathname.startsWith('/admin');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.replace('#', '');
    const scrollToTarget = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const timer = setTimeout(scrollToTarget, 80);
    return () => clearTimeout(timer);
  }, [location.hash, location.pathname]);

  return (
    <div className="App">
      {!hideHeader && <Header />}

      <Routes>
        <Route
          path="/"
          element={
            loading ? null : !isAuthenticated ? (
              <Navigate to="/login" replace />
            ) : (
              <PublicHomePage />
            )
          }
        />
        <Route path="/home" element={<PublicHomePage />} />
        <Route path="/services" element={<Services />} />
        <Route path="/packages/:packageId" element={<PackageDetails />} />
        <Route path="/payment/:packageId" element={<PaymentPage />} />
        <Route path="/payment-success/:packageId" element={<PaymentSuccess />} />
        <Route path="/career" element={<Navigate to="/career/job-apply" replace />} />
        <Route path="/career/job-apply" element={<JobApply />} />
        <Route path="/career/internship-verification" element={<InternshipVerification />} />
        <Route path="/brochure" element={<Brochure />} />
        <Route path="/product" element={<ProductPage />} />
        <Route path="/ProductPage" element={<ProductPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/cart" element={<Navigate to="/dashboard/cart" replace />} />
        <Route
          path="/dashboard/*"
          element={(
            <Suspense fallback={<BrandLoader fullScreen label="Loading your dashboard" />}>
              <RequireAuth>
                <UserDashboard />
              </RequireAuth>
            </Suspense>
          )}
        />
        <Route
          path="/admin/*"
          element={(
            <Suspense fallback={<BrandLoader fullScreen label="Loading admin panel" />}>
              <RequireAdmin>
                <AdminDashboard />
              </RequireAdmin>
            </Suspense>
          )}
        />
        <Route path="/:slug" element={<DynamicPage />} />
      </Routes>

      {!hideFooter && <Footer />}
    </div>
  );
}

function SplashNavigator({ onFinish }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  const handleFinish = () => {
    let hasSession = false;
    try {
      hasSession = Boolean(isAuthenticated || user || localStorage.getItem('sd_auth') === 'true');
    } catch (_) {
      hasSession = Boolean(isAuthenticated || user);
    }

    if (hasSession) {
      if (!location.pathname || location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signup') {
        navigate('/home', { replace: true });
      }
    } else {
      navigate('/login', { replace: true });
    }
    onFinish();
  };

  return <AppSplashScreen onFinish={handleFinish} />;
}

function App() {
  const [showSplash, setShowSplash] = useState(() => !consumeOneTimeSplashSkipFlag());
  const RouterComponent = Capacitor.isNativePlatform() ? HashRouter : BrowserRouter;

  useEffect(() => {
    preloadInterstitialAd();
  }, []);

  const handleSplashFinish = () => {
    setShowSplash(false);
  };

  return (
    <RouterComponent basename={appBase || undefined}>
      {showSplash ? <SplashNavigator onFinish={handleSplashFinish} /> : null}
      <ToastContainer
        position="top-center"
        autoClose={2600}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        newestOnTop
        toastClassName={(context) => `modern-toast modern-toast--${context?.type || 'default'}`}
        bodyClassName="modern-toast-body"
        progressClassName="modern-toast-progress"
      />
      <AppShell>
        <AppLayout />
      </AppShell>
    </RouterComponent>
  );
}

export default App;
